/**
 * CelebrationBurst — the journey's ONE particle system.
 *
 * Particles radiate OUT FROM THE CENTRE of whatever they are placed in, on
 * an even ring of angles, and fade as they travel. Not falling confetti:
 * confetti rains over the whole page and belongs to nothing, while a burst
 * is visibly emitted BY the card that earned it — the celebration points at
 * its own cause.
 *
 * The restraint is deliberate and capped: ≤60 particles, ≤1.2 s, and the
 * tenant's own agent-coin colours. Under `prefers-reduced-motion` the burst is replaced by a
 * static glow rather than removed, so the moment still registers.
 */

import { useMemo } from 'react';
import { useBrand, defaultBrand } from '@/brand';

const PARTICLES = 48;
/** The finale gets the ceiling — larger, never longer. */
const FINALE_PARTICLES = 60;
const MIN_RADIUS_PX = 62;
const RADIUS_SPREAD_PX = 92;
const FINALE_RADIUS_SPREAD_PX = 132;
const SIZE_PX = 6;

/**
 * Fallback only, and imported rather than re-typed: the burst paints with the
 * TENANT's `agentColors`, which is what the agent coins themselves use.
 * Hard-coding the five default hexes here meant the burst matched the default
 * brand and quietly mismatched every tenant that had configured its own —
 * while the comment claimed they were "the same five the avatars use". They
 * are the same five by construction now, in both the configured and the
 * default case.
 */
const FALLBACK_COLORS = defaultBrand.agentColors ?? ['#3b82f6'];

interface Particle {
  dx: number;
  dy: number;
  color: string;
  delayS: number;
  scale: number;
}

function build(count: number, spread: number, colors: string[]): Particle[] {
  return Array.from({ length: count }, (_, i) => {
    // Even angles keep the ring balanced; the radius varies so it reads as a
    // burst rather than a rendered circle.
    const angle = (i / count) * Math.PI * 2;
    const radius = MIN_RADIUS_PX + Math.random() * spread;
    return {
      dx: Math.cos(angle) * radius,
      dy: Math.sin(angle) * radius,
      color: colors[i % colors.length],
      delayS: Math.random() * 0.12,
      scale: 0.6 + Math.random() * 0.8,
    };
  });
}

export function CelebrationBurst({ finale = false }: { finale?: boolean }) {
  const brand = useBrand();
  const colors = brand.agentColors?.length ? brand.agentColors : FALLBACK_COLORS;
  const particles = useMemo(
    () => build(
      finale ? FINALE_PARTICLES : PARTICLES,
      finale ? FINALE_RADIUS_SPREAD_PX : RADIUS_SPREAD_PX,
      colors,
    ),
    [finale, colors],
  );

  return (
    <div className="journey-burst" aria-hidden>
      {particles.map((p, i) => (
        <span
          key={i}
          style={{
            width: SIZE_PX,
            height: SIZE_PX,
            background: p.color,
            // Read by the keyframes — the travel vector for this particle.
            ['--dx' as string]: `${p.dx}px`,
            ['--dy' as string]: `${p.dy}px`,
            ['--s0' as string]: String(p.scale),
            animationDelay: `${p.delayS}s`,
          }}
        />
      ))}
    </div>
  );
}
